import type {
  CloneNotices,
  OwnershipChangeRow,
  OwnershipChangesResponse,
} from './types';
import { collapseNames, counted, listNames } from './utils';

/** The ownership review's tabs. */
export type OwnershipChangesTab = 'added' | 'removed';

/** The ownership changes a ready clone has, as the draft read counts them. */
export type OwnershipChangeCounts = NonNullable<
  CloneNotices['ownershipChanges']
>;

/** Said when every Asset Added is already in the Assets table. */
export const ALL_ADDED_INCLUDED =
  'All eligible added assets have been included.';

/**
 * Whether a clone lost or gained Tracts on its date.
 *
 * @param {OwnershipChangeCounts | null} changes - The draft read's counts
 * @return {boolean} True when there is something to review
 */
export const hasOwnershipChanges = (
  changes: OwnershipChangeCounts | null
): changes is OwnershipChangeCounts =>
  changes !== null && (changes.removed > 0 || changes.added > 0);

/**
 * The review's first tab: Assets added when there are any, else Assets removed.
 *
 * @param {OwnershipChangeCounts | null} changes - The draft read's counts
 * @return {OwnershipChangesTab} The tab to open on
 */
export const reviewTabFor = (
  changes: OwnershipChangeCounts | null
): OwnershipChangesTab =>
  changes !== null && changes.added > 0 ? 'added' : 'removed';

/**
 * The "Original exclusions preserved" notice's text.
 *
 * @param {object} exclusions - The assets and rows the sources leave unticked
 * @param {string[]} sourceNames - The source packages' names, in tag order
 * @return {string} The notice's subtitle
 */
export const preservedExclusionsText = (
  { assets, properties }: NonNullable<CloneNotices['preservedExclusions']>,
  sourceNames: string[]
): string =>
  `${counted(assets, 'asset', 'assets')} and ${counted(
    properties,
    'property',
    'properties'
  )} excluded from ${collapseNames(
    sourceNames
  )} remain unchecked. Re-check a row to include it in the clone.`;

/**
 * The "Asset ownership changes" warning's text: what was removed, then what
 * can be added, each left out at zero.
 *
 * @param {OwnershipChangeCounts} changes - The draft read's counts
 * @return {string} The warning's subtitle
 */
export const ownershipChangesText = ({
  removed,
  removedProperties,
  added,
  addedAvailable,
}: OwnershipChangeCounts): string => {
  const parts: string[] = [];
  if (removed > 0) {
    const properties =
      removedProperties > 0
        ? ` and ${counted(removedProperties, 'property', 'properties')}`
        : '';
    const verb = removed === 1 && properties === '' ? 'was' : 'were';
    parts.push(
      `${counted(removed, 'asset', 'assets')}${properties} ${verb} removed and excluded.`
    );
  }
  if (added > 0) {
    parts.push(
      addedAvailable > 0
        ? `${counted(addedAvailable, 'added asset is', 'added assets are')} available to include.`
        : ALL_ADDED_INCLUDED
    );
  }
  return parts.join(' ');
};

/** A review row's entities' and source packages' names, as the review's table shows them. */
interface OwnershipChangeNames {
  entityNames: string | null;
  packageNames: string | null;
}

/** A review row and its names. */
export type NamedOwnershipChangeRow = OwnershipChangeRow & OwnershipChangeNames;

/**
 * Both lists of the review, each row named from the response's lookups, built
 * once per response: "A, B and C", an entity no longer on file left out.
 *
 * @param {OwnershipChangesResponse} changes - The review's response
 * @return {object} Assets removed and Assets added, named
 */
export const namedOwnershipChanges = ({
  entities,
  packages,
  removed,
  added,
}: OwnershipChangesResponse) => {
  const entityNames = new Map(entities.map(({ id, name }) => [id, name]));
  const packageNames = new Map(packages.map(({ id, name }) => [id, name]));
  /**
   * One row with its names.
   *
   * @param {Row} row - The row
   * @return {object} It, named
   */
  const named = <Row extends OwnershipChangeRow>(
    row: Row
  ): Row & OwnershipChangeNames => ({
    ...row,
    entityNames:
      listNames(row.entityIds.flatMap((id) => entityNames.get(id) ?? [])) ||
      null,
    packageNames:
      listNames(row.packageIds.flatMap((id) => packageNames.get(id) ?? [])) ||
      null,
  });
  return { removed: removed.map(named), added: added.map(named) };
};
